import { ArrowLeftIcon, DotsVerticalIcon } from "@radix-ui/react-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  useNavigate,
  createFileRoute,
  redirect,
  Outlet,
  useMatchRoute,
  Link,
} from "@tanstack/react-router";

import { FeedOperatorCampaginFeedActionsMenu } from "@/components/FeedOperatorCampaginFeedActionsMenu";
import { Page } from "@/components/Page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsTrigger, TabsList } from "@/components/ui/tabs";
import {
  campaignQueryOptions,
  getUnreadCountForInboxQueryOptions,
} from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/view/$algoId/_layout",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { campaignId, algoId },
  }) => {
    const campaign = await queryClient.ensureQueryData(
      campaignQueryOptions(parseInt(campaignId)),
    );
    const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

    if (algo.details.state === "proposed") {
      throw redirect({
        to: `/app/campaigns/$campaignId/review/$algoId`,
        params: { campaignId, algoId },
      });
    } else if (algo.details.state === "denied") {
      throw redirect({ to: "/app/campaigns" });
    }

    await Promise.all([
      queryClient.ensureQueryData(
        getUnreadCountForInboxQueryOptions({
          context: {
            type: "campaign",
            campaign_id: parseInt(campaignId),
            algo_id: parseInt(algoId),
          },
        }),
      ),
    ]);
  },
});

function RouteComponent() {
  const { campaignId, algoId } = Route.useParams();
  const { data: campaign } = useSuspenseQuery(
    campaignQueryOptions(parseInt(campaignId)),
  );
  const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;
  const matchRoute = useMatchRoute();
  const isIndex = matchRoute({ to: "/app/campaigns/$campaignId/view/$algoId" });
  const navigate = useNavigate({
    from: "/app/campaigns/$campaignId/review/$algoId",
  });

  return (
    <Page
      className={cn(
        "max-md:pb-0",
        !isIndex && "h-screen max-md:overflow-hidden",
      )}
    >
      <div
        className={cn(
          "w-full flex flex-col gap-4 mx-auto",
          !isIndex && "h-full",
        )}
      >
        <div className="border-b border-slate-6 dark:border-slatedark-6 pb-4 flex flex-col gap-4 items-start">
          <div className="flex items-center justify-between gap-8 self-stretch">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-2 self-stretch">
                <Link
                  className="flex items-center gap-2 cursor-pointer-extra"
                  to="/app/campaigns/$campaignId/view"
                  params={{ campaignId }}
                >
                  <ArrowLeftIcon />
                  <span className="text-slate-dim text-xs md:text-base">
                    Campaign: {campaign.display_name}
                  </span>
                </Link>
                <h1 className="font-bold text-3xl md:text-6xl instrument-serif">
                  {algo.display_name}
                </h1>
              </div>
            </div>

            <FeedOperatorCampaginFeedActionsMenu
              feed={algo}
              campaign={campaign}
            >
              <Button variant="outline" size="icon">
                <span className="sr-only">Actions</span>
                <DotsVerticalIcon />
              </Button>
            </FeedOperatorCampaginFeedActionsMenu>
          </div>
          <Tabs
            value={isIndex ? "index" : "messages"}
            className="flex flex-col items-start gap-4 md:gap-6"
            onValueChange={(value) => {
              void navigate({
                params: { campaignId, algoId },
                to:
                  value === "index"
                    ? "/app/campaigns/$campaignId/view/$algoId"
                    : "/app/campaigns/$campaignId/view/$algoId/messages",
              });
            }}
          >
            <TabsList>
              <TabsTrigger value="index">Analytics</TabsTrigger>
              <TabsTrigger value="messages" className="gap-1">
                Messages
                {algo.details.unread_message_count > 0 && (
                  <Badge variant="destructive" className="ml-2" noIcon>
                    {algo.details.unread_message_count}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex flex-col gap-4 md:gap-8 flex-1 min-h-0">
          <Outlet />
        </div>
      </div>
    </Page>
  );
}
